<template>
  <layout-basic>
    <div class="single-course-banner">
      <h1>{{ course.title }}</h1>
      <p>{{ course.description }}</p>
    </div>
    <v-container class="margin-single-page">
      <v-row>
        <v-col cols="12" md="6">
          <div class="course-single-wrapper">
            <div class="course-banner">{{ course.category }}</div>
            <h1>{{ course.title }}</h1>
            <h3>Course Overview:</h3>
            <p v-html="course.singlePage.description"></p>
            <a class="quest-btn" href="/watch-demo">Watch the 5-minute demo</a>
          </div>
        </v-col>
        <v-col cols="12" md="6" class="d-flex justify-md-end justify-center">
          <div class="course-objective-wrapper">
            <h3>Course Objective{{ course.singlePage.objectives.length > 1 ? 's' : '' }}</h3>
            <div class="course-objectives">
                <div  v-for="objective in course.singlePage.objectives" class="course-objective" v-bind:key="objective">
                    <img
                    class="logo mr-2"
                    src="/static/icons/benefits/induction-training-software-for-businesses.png"
                    style="height: 18px; width: 18px; padding: 0px;"
                    alt="Star"
                    />
                    <div class="objective-text" v-html="objective" >
                    </div>
                </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>
    <section class="animate-cat-on-scroll">
      <v-row no-gutters>
        <v-col no-gutters>
          <!-- Shape Background -->
          <div class="demo-background">
            <img
              class="demo-background-image"
              :src="
                getImageSource(
                  'improve-demo-section-background.png',
                  'homepage/demo'
                )
              "
            />
            <img
              class="demo-background-cat"
              :src="
                getImageSource(
                  'training-courses-for-smes-with-improve.png',
                  'homepage/demo'
                )
              "
            />
            <div class="demo-content">
              <h2>Back to Course Library</h2>
              <p>
                See all course categories and courses.
              </p>
              <a href="/course-library" class="book-demo-btn">
                Show me
              </a>
            </div>
          </div>
        </v-col>
      </v-row>
    </section>
  </layout-basic>
</template>

<script>
import LayoutBasic from '@/layouts/Dashboard';
import CourseList from '@/../public/static/newcourselist.json';
import { screensize } from '../../utils/screensize';

const getCurrentScreenSize = screensize();
const currentScreenSize = getCurrentScreenSize();

const complianceKeywords = "Compliance, Alcohol and Drugs at Work, E-Privacy, Anti-Bribery and Corruption, Equality Diversity Inclusion, Cyber Essentials, Fraud Prevention, Environmental Awareness, Worker Protection Act";
const complianceGuideKeywords = "Compliance Management Guide, Anti-Bribery and Corruption Business Owners, Cyber Essentials Business Owners, Fraud Prevention Business Owners";
const healthSafetyKeywords = "Health and Safety, Asbestos Awareness, Fire Safety, COSHH, Working at Height, Manual Handling, Lone Working, Driver Safety Awareness, First Aid Awareness, Display Screen Equipment";
const itSkillsKeywords = "IT Skills, Excel Beginners, Excel Intermediate, Excel Advanced, Excel Array Functions Advanced, Outlook Beginners, Outlook Intermediate, Outlook Advanced, PowerPoint Beginners, PowerPoint Intermediate, PowerPoint Advanced, Word Beginners, Word Intermediate, Word Advanced";
const managementLeadershipKeywords = "Management and Leadership, Effective Communication, HR for Non-HR, Effective Leadership, Leading Essentials, Handling Difficult Conversations, Planning and Allocating Work, Understanding Performance Management, Managing Equality and Diversity, Setting Team Objectives, Managing Yourself";
const peopleRelationshipsKeywords = "People and Relationships, Emotional Intelligence, Conflict Resolution, Team Building, Workplace Relationships";
const salesKeywords = "Sales, Introduction to Sales, Sales Strategy, Customer Relationship Management, Closing Sales Techniques, Negotiation Skills";
const workingFromHomeKeywords = "Working from Home, Home Office Setup, Remote Collaboration, Time Management Remote, Staying Healthy Remote Work";
const projectManagementKeywords = "Project Management, Introduction to Project Management, Agile Methodologies, Resource Planning, Risk Management, Delivering Successful Projects";

export default {
  name: 'SinglePage',
  components: {
    LayoutBasic,
  },
  data() {
    return {
      CourseList,
      id: this.$route.params.id,
      // Shaped like a real entry so the FIRST render (before mounted() looks
      // the course up) does not throw on course.singlePage.description. Vue
      // recovered on the next render, but every course page logged a render
      // error on load and the prerender hydration gate flags exactly that.
      course: { singlePage: { description: '', objectives: [] } },
      screenSize: currentScreenSize,
    };
  },
  metaInfo() {
    let keywords = '';
    switch (this.course.type) {
      case 'Compliance':
        keywords = complianceKeywords;
        break;
      case 'Compliance / Management Guide':
        keywords = complianceGuideKeywords;
        break;
      case 'Health and Safety':
        keywords = healthSafetyKeywords;
        break;
      case 'IT Skills':
        keywords = itSkillsKeywords;
        break;
      case 'Management and Leadership':
        keywords = managementLeadershipKeywords;
        break;
      case 'People and Relationships':
        keywords = peopleRelationshipsKeywords;
        break;
      case 'Sales':
        keywords = salesKeywords;
        break;
      case 'Working from Home':
        keywords = workingFromHomeKeywords;
        break;
      case 'Project Management':
        keywords = projectManagementKeywords;
        break;
      default:
        keywords = 'Online Training, Improve Courses, Professional Development';
    }

    return {
      title: this.course.title + ' | Improve Online',
      meta: [
        // Meta Description
        { name: 'description', content: this.course.description },

        // Keywords
        { name: 'keywords', content: keywords },

        // Open Graph / Facebook Tags
        { property: 'og:title', content: this.course.title },
        { property: 'og:description', content: this.course.description },
        { property: 'og:image', content: `https://www.improve.online/static/SEO.png` },
        { property: 'og:url', content: `https://www.improve.online/course/${this.$route.params.id}` },

        // Twitter Card Tags
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: this.course.title },
        { name: 'twitter:description', content: this.course.description },
        { name: 'twitter:image', content: `https://www.improve.online/static/SEO.png` },
      ],
      link: [
        // Canonical URL
        { rel: 'canonical', href: `https://www.improve.online/course/${this.$route.params.id}` },
      ],
    };
  },
  mounted() {
    this.allCourses = this.CourseList;
    this.getCourse(this.id);
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('scroll', this.handleScroll);
    this.handleScroll();
  },
  methods: {
    getCourse(id) {
      var flag = false;
      for (var singleCourse in this.allCourses) {
        for (let newCourse in this.allCourses[singleCourse]) {
          if (newCourse == id) {
            this.course = this.allCourses[singleCourse][newCourse];
            this.course.category = this.addSpacesToCamelCase(singleCourse);
            flag = true;
            break;
          }
        }
        if (flag) {
          break;
        }
      }
      if (!flag) {
        this.$router.push('/course-library');
      }
    },
    handleResize() {
      this.screenSize = getCurrentScreenSize();
    },
    getImageSource(imageName, filepath) {
      return `/static/${filepath}/${this.screenSize}/${imageName}`;
    },
    handleScroll() {
      const catElemet = this.$el.querySelector('.animate-cat-on-scroll');
      const catRect = catElemet.getBoundingClientRect();
      const catInViewport =
        catRect.top >= 0 &&
        catRect.left >= 0 &&
        catRect.bottom <=
          (window.innerHeight || document.documentElement.clientHeight) &&
        catRect.right <=
          (window.innerWidth || document.documentElement.clientWidth);

      if (catInViewport) {
        catElemet.classList.add('animate');
      }
    },
    addSpacesToCamelCase(input) {
      return input.replace(/([a-z])([A-Z])/g, '$1 $2');
    },
  },
  watch: {
    $route(to) {
      this.id = to.params.id;
      this.getCourse(this.id);
    },
  },
};
</script>

<style>
.single-course-banner {
  background-image: url(/static/single-page/banner.png);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 396px;
  width: 100%;
  margin-top: 167px;
}

.single-course-banner h1 {
  font-size: 50px;
  font-weight: 700;
  color: #002458;
  margin-bottom: 20px;
  max-width: 1420px;
  text-align: center;
}

.single-course-banner p {
  font-size: 16px;
  font-weight: 400;
  color: #002458;
  max-width: 918px;
  text-align: center;
}

.course-banner {
    width: fit-content;
    padding: 0px 30px;
  height: 26px;
  background-color: #08a0a8;
  color: #002458;
  font-weight: 700;
  font-size: 10px;
  letter-spacing: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 25px;
  margin-bottom: 20px;
  text-transform: uppercase;
}

.course-single-wrapper h1 {
  font-size: 35px;
  font-weight: 700;
  color: #002458;
  margin-bottom: 20px;
}

.course-single-wrapper h3 {
  font-size: 22px;
  font-weight: 700;
  color: #078f96;
  margin-bottom: 20px;
}

.margin-single-page {
 margin: 50px auto;
}

.course-single-wrapper p {
  font-size: 16px;
  font-weight: 400;
  color: #002458;
  margin-bottom: 40px;
}

.quest-btn {
  background-color: #08a0a8;
  border: 2px solid #08a0a8 !important;
  color: #fff !important;
  padding: 7px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

.free-trial-btn:hover {
  opacity: 0.8 !important;
}

.course-objective-wrapper {
  max-width: 476px;
  width: 100%;
  border: 2px solid #078f96;
  background: #f8f8f8;
  padding: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 7px;
}

.course-objective-wrapper h3 {
  font-size: 28px;
  font-weight: 700;
  color: #078f96;
  margin-bottom: 20px;
}

.course-objective {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.objective-text {
  font-size: 16px;
  font-weight: 400;
  color: #333333;
}

@media screen and (max-width: 960px) {
    .single-course-banner {
        margin-top: 127px;
    }

    .single-course-banner h1 {
        font-size: 30px;
    }

    .single-course-banner p {
        font-size: 14px;
    }

    .course-single-wrapper {
        text-align: center;
    }
    
    .course-banner {
        margin: 0 auto 20px;
    }

    .margin-single-page {
        margin: 0px auto;
    }
}
</style>
